/**
 * Sortable, alternating-row item table. Pure view.
 *
 * Renders both one-off transactions and recurring rules in a single
 * unified table — the shell passes the already-merged + sorted `items`
 * array out of `useTransactions`. Transfers collapse to one row
 * (source leg only) and render an arrow between the two accounts.
 *
 * Empty state ("No items found...") lives here too so the shell only
 * needs to wire props in one place.
 *
 * SortableTh is a private atom — only this view sorts.
 */
import React from 'react';
import { motion } from 'framer-motion';
import {
  ArrowDown, ArrowLeftRight, ArrowUp, ArrowUpDown, Edit2, Plus, Trash2, Upload,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import CategoryColorPicker from '../../components/CategoryColorPicker';
import { FADE_IN_HIDDEN, FADE_IN_SHOWN } from '../../lib/animations';
import { fmtMoney } from '../../lib/format';
import { toUIType } from '../../lib/liabilityType';
import { formatFrequency } from './constants';

const SortableTh = ({ column, label, sortConfig, onSort, align = 'left' }) => {
  const active = sortConfig.column === column;
  const Icon = active ? (sortConfig.direction === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  const thAlign = align === 'right' ? 'text-right' : 'text-left';
  return (
    <th className={`${thAlign} px-6 py-4`}>
      <button
        type="button"
        onClick={() => onSort(column)}
        className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] font-semibold transition-colors ${
          active ? 'text-[#0052FF]' : 'text-[#64748B] hover:text-[#020617]'
        }`}
        data-testid={`sort-${column}`}
        aria-label={`Sort by ${label}`}
      >
        {label}
        <Icon className={`w-3 h-3 ${active ? 'opacity-100' : 'opacity-40'}`} />
      </button>
    </th>
  );
};

const TransactionsTable = ({
  items, sortConfig, toggleSort,
  accountNameMap, accountKindMap,
  handleEdit, handleDelete,
  openNewDialog, setImportDialogOpen,
}) => {
  if (items.length === 0) {
    return (
      <div className="bg-white border border-[#E2E8F0] rounded-lg p-12 text-center">
        <p className="text-[#64748B] mb-4">No items found. Add your first transaction or import from a file.</p>
        <div className="flex gap-3 justify-center flex-wrap">
          <Button onClick={openNewDialog} className="bg-[#0052FF] text-white hover:bg-[#003ECC]">
            <Plus className="w-4 h-4 mr-2" />
            Add Transaction
          </Button>
          <Button onClick={() => setImportDialogOpen(true)} className="bg-[#F1F5F9] text-[#020617] hover:bg-[#E2E8F0]">
            <Upload className="w-4 h-4 mr-2" />
            Import File
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
            <tr>
              <SortableTh column="date" label="Date" sortConfig={sortConfig} onSort={toggleSort} />
              <SortableTh column="description" label="Description" sortConfig={sortConfig} onSort={toggleSort} />
              <SortableTh column="frequency" label="Frequency" sortConfig={sortConfig} onSort={toggleSort} />
              <SortableTh column="category" label="Category" sortConfig={sortConfig} onSort={toggleSort} />
              <SortableTh column="account" label="Account" sortConfig={sortConfig} onSort={toggleSort} />
              <SortableTh column="amount" label="Amount" sortConfig={sortConfig} onSort={toggleSort} align="right" />
              <th className="text-right px-6 py-4 text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B] print-hide">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <motion.tr key={it.key} initial={FADE_IN_HIDDEN} animate={FADE_IN_SHOWN} className="border-b border-[#E2E8F0] last:border-0 hover:bg-[#F8FAFC] transition-colors" data-testid={`item-row-${it.key}`}>
                <td className="px-6 py-4 text-sm text-[#334155]">
                  {new Date(it.date).toLocaleDateString('en-GB')}
                  {it.mechanic === 'recurring' && it.end_date && (
                    <div className="text-xs text-[#64748B] mt-0.5">until {new Date(it.end_date).toLocaleDateString('en-GB')}</div>
                  )}
                </td>
                <td className="px-6 py-4 text-sm font-medium text-[#020617]">
                  <span className="inline-flex items-center gap-1.5 flex-wrap">
                    <span>{it.description}</span>
                    {it.transfer_id && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-[#E0E7FF] text-[#3730A3]" title="Transfer between accounts" data-testid={`transfer-badge-${it.key}`}>
                        <ArrowLeftRight className="w-2.5 h-2.5" />
                        Transfer
                      </span>
                    )}
                    {it.is_estimated && (
                      <span className="inline-flex items-center justify-center h-5 px-2 rounded text-[10px] font-semibold uppercase tracking-wider bg-[#FEF3C7] text-[#92400E] leading-none align-middle" title="Estimated amount" data-testid={`estimated-badge-${it.key}`}>
                        EST
                      </span>
                    )}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-[#334155]" data-testid={`item-frequency-${it.key}`}>
                  {it.mechanic === 'recurring' ? formatFrequency(it.frequency) : 'One-off'}
                </td>
                <td className="px-6 py-4 text-sm text-[#64748B]">
                  {it.category ? (
                    <span className="inline-flex items-center gap-2">
                      <CategoryColorPicker category={it.category} />
                      {it.category}
                    </span>
                  ) : '-'}
                </td>
                <td className="px-6 py-4 text-sm text-[#334155]" data-testid={`item-account-${it.key}`}>
                  {it.transfer_id ? (
                    <span>
                      {accountNameMap[it.account_id] || '-'}
                      <span className="mx-1.5 text-[#94A3B8]">→</span>
                      {accountNameMap[it.transfer_counter_account_id] || '-'}
                    </span>
                  ) : (accountNameMap[it.account_id] || '-')}
                </td>
                <td className={`px-6 py-4 text-sm text-right font-semibold ${
                  it.transfer_id ? 'text-[#475569]' : (toUIType(it.transaction_type, accountKindMap[it.account_id]) === 'income' ? 'text-[#10B981]' : 'text-[#EF4444]')
                }`}>
                  {it.transfer_id ? `£${fmtMoney(it.amount)}` : `${toUIType(it.transaction_type, accountKindMap[it.account_id]) === 'income' ? '+' : '-'}£${fmtMoney(it.amount)}`}
                </td>
                <td className="px-6 py-4 text-right print-hide">
                  <div className="flex justify-end gap-2 print-hide">
                    <button onClick={() => handleEdit(it)} className="text-[#64748B] hover:text-[#0052FF] transition-colors" data-testid={`edit-item-${it.id}`}>
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(it)} className="text-[#64748B] hover:text-[#EF4444] transition-colors" data-testid={`delete-item-${it.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TransactionsTable;
